import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronDown, ChevronUp, Check, Edit2 } from 'lucide-react';
import InputField from '../../ui/InputField';
import {
  selectRewardForm,
  updateRewardForm,
  openSelectTierModal,
} from '../../../store/slices/gamificationSlice';

const RewardTypeSection = () => {
  const dispatch = useDispatch();
  const rewardForm = useSelector(selectRewardForm);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const rewardTypeOptions = [
    { value: 'FLAT_BONUS', label: 'Flat $X bonus' },
    { value: 'COMMISSION_TIER', label: 'Upgrade Commission Tier' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRewardTypeChange = (value) => {
    dispatch(updateRewardForm({ field: 'rewardType', value }));
    // Reset reward config when changing type
    dispatch(updateRewardForm({ field: 'rewardConfig.bonusAmount', value: '' }));
    dispatch(updateRewardForm({ field: 'rewardConfig.tierId', value: null }));
    dispatch(updateRewardForm({ field: 'rewardConfig.tierName', value: '' }));
  };

  const getRewardDisplay = () => {
    if (!rewardForm.rewardType) return 'Select a reward';

    switch (rewardForm.rewardType) {
      case 'FLAT_BONUS':
        return rewardForm.rewardConfig.bonusAmount
          ? `Flat $${rewardForm.rewardConfig.bonusAmount} Bonus`
          : 'Flat $X bonus';
      case 'COMMISSION_TIER':
        return rewardForm.rewardConfig.tierName
          ? `Upgrade to ${rewardForm.rewardConfig.tierName}`
          : 'Upgrade Commission Tier';
      default:
        return 'Select a reward';
    }
  };

  const isSaveDisabled = () => {
    if (!rewardForm.rewardType) return true;

    switch (rewardForm.rewardType) {
      case 'FLAT_BONUS':
        return !rewardForm.rewardConfig.bonusAmount;
      case 'COMMISSION_TIER':
        return !rewardForm.rewardConfig.tierId;
      default:
        return true;
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-600">
        Reward with <span className="text-red-500">*</span>
      </label>

      <div className="relative w-full" ref={dropdownRef}>
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`
            w-full px-4 py-2.5 rounded-lg border-2 transition-all duration-200 text-sm
            flex items-center justify-between
            ${isOpen ? 'border-purple-500' : 'border-gray-300'}
            bg-white hover:border-purple-400
            focus:outline-none
          `}
        >
          <span className={rewardForm.rewardType ? 'text-gray-900' : 'text-gray-400'}>
            {getRewardDisplay()}
          </span>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-[9999] w-full mt-2 bg-white border-2 border-purple-500 rounded-lg shadow-lg overflow-hidden">
            {rewardTypeOptions.map((option) => {
              const isSelected = option.value === rewardForm.rewardType;
              return (
                <div key={option.value}>
                  <button
                    type="button"
                    onClick={() => handleRewardTypeChange(option.value)}
                    className={`
                      w-full px-4 py-2.5 text-left flex items-center justify-between text-sm
                      transition-colors duration-150
                      ${isSelected ? 'bg-purple-50 text-purple-600' : 'text-gray-700 hover:bg-gray-50'}
                    `}
                  >
                    <span className={isSelected ? 'font-medium' : ''}>{option.label}</span>
                    {isSelected && <Check className="w-4 h-4 text-purple-600" />}
                  </button>

                  {/* Inline Input for Flat Bonus */}
                  {isSelected && option.value === 'FLAT_BONUS' && (
                    <div className="px-4 pb-3 bg-purple-50">
                      <InputField
                        prefix="$"
                        placeholder="e.g. 100"
                        type="number"
                        value={rewardForm.rewardConfig.bonusAmount}
                        onChange={(value) =>
                          dispatch(updateRewardForm({ field: 'rewardConfig.bonusAmount', value }))
                        }
                      />
                    </div>
                  )}

                  {/* Inline Button for Commission Tier */}
                  {isSelected && option.value === 'COMMISSION_TIER' && (
                    <div className="px-4 pb-3 bg-purple-50">
                      <button
                        type="button"
                        onClick={() => {
                          dispatch(openSelectTierModal());
                          setIsOpen(false);
                        }}
                        className={`
                          w-full px-4 py-2.5 rounded-lg border-2 transition-all duration-200 text-sm
                          flex items-center justify-between
                          ${rewardForm.rewardConfig.tierName ? 'border-purple-500' : 'border-gray-300'}
                          hover:border-purple-400 bg-white
                        `}
                      >
                        <span className={rewardForm.rewardConfig.tierName ? 'text-gray-900' : 'text-gray-400'}>
                          {rewardForm.rewardConfig.tierName || 'Select a tier'}
                        </span>
                        {rewardForm.rewardConfig.tierName && (
                          <Edit2 className="w-4 h-4 text-purple-600" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Save/Cancel Buttons at Bottom */}
            {rewardForm.rewardType && (
              <div className="px-4 py-3 flex gap-3 border-t border-gray-200 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    dispatch(updateRewardForm({ field: 'rewardConfig.bonusAmount', value: '' }));
                    dispatch(updateRewardForm({ field: 'rewardConfig.tierId', value: null }));
                    dispatch(updateRewardForm({ field: 'rewardConfig.tierName', value: '' }));
                    dispatch(updateRewardForm({ field: 'rewardType', value: null }));
                  }}
                  className="flex-1 px-4 py-2.5 bg-white border-2 border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  disabled={isSaveDisabled()}
                  className={`
                    flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors
                    ${isSaveDisabled()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary/90'
                    }
                  `}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardTypeSection;
